/**
 * The interface's language.
 *
 * Chosen in this order: what this browser last chose, then the browser's own
 * preference, then English. The account's saved choice then overrides it once
 * the person is signed in (`useLocaleSync`), so a choice made on one device
 * follows them to the next — and to the emails we send them.
 *
 * Bundled, not lazy-loaded: two languages are small, and loading them on
 * demand would flash English before French on every cold start.
 */
import { useCallback, useEffect } from "react";
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import { DEFAULT_LOCALE, isLocale, localizeServerText, type Locale } from "@rec/shared";
import { NAMESPACES, resources } from "./resources";

const STORAGE_KEY = "locale";
/**
 * Set when the language changes and the account has not been told yet —
 * including a change made on the sign-in page, before there is an account to
 * tell. Cleared by `useLocaleSync` once the server has it.
 */
const PENDING_KEY = "locale:unsynced";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    // Private mode or blocked storage: the choice lasts the session only.
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* see read() */
  }
}

function fromBrowser(): Locale | null {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const base = tag?.slice(0, 2).toLowerCase();
    if (isLocale(base)) return base;
  }
  return null;
}

export function detectLocale(): Locale {
  const stored = read(STORAGE_KEY);
  return isLocale(stored) ? stored : (fromBrowser() ?? DEFAULT_LOCALE);
}

/**
 * Keep `<html lang>` true.
 *
 * Screen readers pick their voice from it, and Chrome uses it to decide
 * whether to offer Google Translate — a French page still declared `en` gets
 * offered a translation into the language it is already in.
 */
function applyDocumentLanguage(locale: string) {
  document.documentElement.lang = locale;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: detectLocale(),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: ["en", "fr"],
  ns: NAMESPACES,
  defaultNS: "common",
  // React escapes on render; escaping here as well would show `&amp;`.
  interpolation: { escapeValue: false },
  returnNull: false,
  react: { useSuspense: false },
});

applyDocumentLanguage(i18n.language);
i18n.on("languageChanged", applyDocumentLanguage);

export { i18n };

export function currentLocale(): Locale {
  return isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
}

/**
 * Switch language now, remember it on this device, and queue it for the
 * account. `fromAccount` is for adopting the account's saved choice, which by
 * definition needs no syncing back.
 */
export async function setLocale(locale: Locale, options?: { fromAccount?: boolean }) {
  write(STORAGE_KEY, locale);
  if (!options?.fromAccount) write(PENDING_KEY, locale);
  if (i18n.language !== locale) await i18n.changeLanguage(locale);
}

/** The current language, re-rendering when it changes. */
export function useLocale(): Locale {
  const { i18n: instance } = useTranslation();
  return isLocale(instance.language) ? instance.language : DEFAULT_LOCALE;
}

/**
 * Translate a sentence the server wrote — an error, a status explanation, a
 * stored failure reason. Unknown sentences come back unchanged, so the worst
 * case is English, never a blank.
 */
export function useServerText(): (text: string | null | undefined) => string {
  const locale = useLocale();
  // Stable per language: callers use it in memo and query `select`
  // dependencies, where a new function every render would recompute forever.
  return useCallback((text) => localizeServerText(text ?? "", locale), [locale]);
}

/** The same, outside React (error formatting, toasts). */
export function serverText(text: string | null | undefined): string {
  return localizeServerText(text ?? "", currentLocale());
}

/**
 * Reconcile this device with the account, once signed in.
 *
 *  - a change not yet synced (made here, maybe before signing in) wins and is
 *    saved to the account;
 *  - otherwise the account's saved choice wins, so the language follows the
 *    person across devices;
 *  - an account with no choice yet is given this device's.
 *
 * `save` is injected so this module does not depend on the API client.
 */
export function useLocaleSync(
  account: { locale: string | null } | undefined,
  save: (locale: Locale) => Promise<unknown>,
) {
  const locale = useLocale();

  useEffect(() => {
    if (!account) return;
    const pending = read(PENDING_KEY);

    if (isLocale(pending)) {
      if (pending === account.locale) {
        write(PENDING_KEY, null);
        return;
      }
      // Left set on failure, so the next load tries again.
      void save(pending).then(() => write(PENDING_KEY, null)).catch(() => {});
      return;
    }

    if (isLocale(account.locale)) {
      if (account.locale !== locale) void setLocale(account.locale, { fromAccount: true });
      return;
    }

    void save(locale).catch(() => {});
    // `locale` is deliberately a dependency: a change made while signed in
    // sets the pending flag and must be saved straight away.
  }, [account, locale, save]);
}
